/* eslint-disable ember/use-ember-get-and-set */
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import type { LatLng, LatLngBounds, Marker, Polygon, Polyline, Map as LeafletMap } from 'leaflet';
import leafletNs from 'leaflet';

interface RenderMapOptions {
  leafletMap: LeafletMap;
  bounds: LatLngBounds;
  mapScale?: number;
  iconScale?: number;
  labelScale?: number;
  markerNames?: Record<string, string>;
}

// --- Marker classification ---

type MarkerInfo =
  | { kind: 'resource-icon'; svgPath: string; size: number }
  | { kind: 'monogram'; initials: string; bgColor: string; size: number }
  | { kind: 'profile-picture'; tooltipName: string; size: number }
  | { kind: 'current-user'; svgPath: string; label: string; size: number }
  | { kind: 'room-label'; name: string; color: string; svgPath: string; size: number }
  | { kind: 'unknown'; size: number };

const RESOURCE_TYPE_SVG: Record<string, string> = {
  desk: 'desk-icon.svg',
  parking: 'parking-icon.svg',
  room: 'room-icon.svg',
  elevator: 'elevator-icon.svg',
  stairs: 'stairs-icon.svg',
  AED: 'AED-icon.svg',
  'fire-extinguisher': 'fire-extinguisher-icon.svg',
  'first-aid': 'first-aid-icon.svg',
  printer: 'printer-icon.svg',
  restroom: 'restroom-icon.svg',
  'break-room': 'break-room-icon.svg',
  cafe: 'cafe-icon.svg',
  kitchen: 'kitchen-icon.svg',
  'mothers-room': 'mothers-room-icon.svg',
  custom: 'custom-resource-icon.svg',
  'visitor-area': 'visitor-area-icon.svg',
  'delivery-area': 'delivery-area-icon.svg',
  'emergency-exit': 'emergency-exit-icon.svg',
  accessible: 'accessible-icon.svg',
};

const DISABLED_RESOURCE_TYPE_SVG: Record<string, string> = {
  desk: 'disabled-desk-icon.svg',
  parking: 'disabled-parking-icon.svg',
  room: 'disabled-room-icon.svg',
  elevator: 'disabled-elevator-icon.svg',
  stairs: 'disabled-stairs-icon.svg',
  AED: 'disabled-AED-icon.svg',
  'fire-extinguisher': 'disabled-fire-extinguisher-icon.svg',
  'first-aid': 'disabled-first-aid-icon.svg',
  printer: 'disabled-printer-icon.svg',
  restroom: 'disabled-restroom-icon.svg',
  'break-room': 'disabled-break-room-icon.svg',
  cafe: 'disabled-cafe-icon.svg',
  kitchen: 'disabled-kitchen-icon.svg',
  'mothers-room': 'disabled-mothers-room-icon.svg',
  custom: 'disabled-custom-resource-icon.svg',
  'visitor-area': 'disabled-visitor-area-icon.svg',
  'delivery-area': 'disabled-delivery-area-icon.svg',
  'emergency-exit': 'disabled-emergency-exit-icon.svg',
  accessible: 'disabled-accessible-icon.svg',
};

const SVG_BASE_PATH = '/assets/images/resource-map/';

/** Derive 1–4 character initials from a name or email (mirrors monogramInitials in map-marker-html.js) */
export function deriveInitials(input: string): string {
  const name = input ?? '';
  if (name.includes('@')) {
    const prefix = name.split('@')[0]!;
    return prefix
      .split(/[.\-_+]/)
      .filter(Boolean)
      .map((p) => p.charAt(0))
      .join('')
      .slice(0, 2);
  }
  // `+N` strip is for "Alice +2 others" tooltip strings — only applies to the name branch
  const cleanName = /\+\d+/.test(name) ? name.split('+')[0]! : name;
  return cleanName
    .split(' ')
    .map((w) => w.charAt(0))
    .join('')
    .slice(0, 4);
}

/** Extract tooltip text from a Leaflet marker (the employee/desk name shown on hover) */
function getMarkerTooltipText(marker: Marker): string {
  const tooltip = (
    marker as unknown as { getTooltip?: () => { getContent?: () => string | HTMLElement | null } }
  ).getTooltip?.();
  if (!tooltip) return '';
  const content = tooltip.getContent?.();
  if (!content) return '';
  // ember-leaflet sets tooltip content to a DOM element, not a string
  if (typeof content === 'string') {
    return content.replace(/<[^>]*>/g, '').trim();
  }
  if (content instanceof HTMLElement) {
    return (content.textContent || '').trim();
  }
  return '';
}

/** Read the feature ID from the marker's DOM element (set by _registerMarkerTestSelectors) */
function getMarkerFeatureId(marker: Marker): string | null {
  const el = (marker as unknown as { _icon?: HTMLElement })._icon;
  return el?.dataset?.['testFeatureId'] || null;
}

function classifyMarker(marker: Marker, markerNames?: Record<string, string>): MarkerInfo {
  const icon = marker.getIcon();
  const opts = icon?.options || {};
  const className = (opts as { className?: string }).className || '';
  const html = String((opts as { html?: string }).html || '');
  const iconSize = (opts as { iconSize?: number[] }).iconSize;
  const size = iconSize?.[0] || 30;

  // Current user's assigned/reserved desk: inner HTML has data-test-current-user
  // with a reserved-desk-icon or reserved-parking-icon and a label
  if (html.includes('data-test-current-user')) {
    const labelMatch = html.match(/reserved-desk-label[^>]*>([^<]+)</);
    const label = labelMatch?.[1]?.trim() || '';
    if (html.includes('reserved-parking-icon')) {
      return { kind: 'current-user', svgPath: SVG_BASE_PATH + 'red-parking-icon.svg', label, size };
    }
    return { kind: 'current-user', svgPath: SVG_BASE_PATH + 'red-desk-icon.svg', label, size };
  }

  // Profile picture: has an <img> with data-test-profile-picture.
  // Cross-origin photos can't be drawn to canvas without tainting it, and
  // production CDNs don't reliably support CORS. Instead of attempting to
  // load the photo, we render a monogram using the employee name.
  // NOTE: must check before resource-icon-base since all markers on the live
  // map share that className — the html content distinguishes marker types.
  if (html.includes('data-test-profile-picture')) {
    // Look up employee name from the pre-built markerNames map (keyed by feature ID),
    // falling back to tooltip text if available.
    const featureId = getMarkerFeatureId(marker);
    const name = (featureId && markerNames?.[featureId]) || getMarkerTooltipText(marker);
    return { kind: 'profile-picture', tooltipName: name, size };
  }

  // Monogram: has data-test-monogram or data-test-entry-monogram
  // NOTE: must check before resource-icon-base since all markers on the live
  // map share that className — the html content distinguishes marker types.
  if (html.includes('data-test-monogram') || html.includes('data-test-entry-monogram')) {
    // The inner monogram div looks like:
    //   <div data-test-entry-monogram style="...">AB</div>
    // Capture the text content between the closing > and the next <
    const initialsMatch = html.match(/data-test-entry-monogram[\s\S]*?>\s*([^<]+)/);
    let initials = initialsMatch?.[1]?.trim() || '';
    if (!initials) {
      const stripped = html.replace(/<[^>]*>/g, '').trim();
      if (stripped.length <= 4) initials = stripped;
    }
    // Fall back to markerNames lookup if regex extraction failed
    if (!initials) {
      const featureId = getMarkerFeatureId(marker);
      const fullName = (featureId && markerNames?.[featureId]) || '';
      initials = deriveInitials(fullName);
    }
    const bgMatch = html.match(/background-color:\s*([^;"]+)/);
    const bgColor = bgMatch?.[1]?.trim() || 'hsl(224, 8%, 48%)';
    return { kind: 'monogram', initials: initials || '?', bgColor, size };
  }

  // Disabled desk/parking: inner HTML has data-test-disabled-desk or data-test-disabled-parking
  // These have the disabled icon class in the inner HTML, not on the DivIcon wrapper
  if (html.includes('data-test-disabled-desk')) {
    return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + 'disabled-desk-icon.svg', size };
  }
  if (html.includes('data-test-disabled-parking')) {
    return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + 'disabled-parking-icon.svg', size };
  }

  // Room/area label: has map-polygon-label class with a room name
  // Rendered as a DivIcon at the polygon center with the room icon + name label
  if (html.includes('map-polygon-label')) {
    // Extract room name from the label div
    const nameMatch = html.match(/data-test-feature-label[^>]*>\s*([^<]*)/);
    const name = nameMatch?.[1]?.trim() || '';
    // Extract label color class: text-cilantro-40 (green/available), text-red-40 (unavailable), text-carbon-30 (disabled)
    const colorMap: Record<string, string> = {
      'cilantro-40': '#21944e',
      'cilantro-30': '#4ec176',
      'red-40': '#FA4338',
      'carbon-30': '#a4a9b6',
    };
    let color = '#21944e'; // default green
    for (const [cls, hex] of Object.entries(colorMap)) {
      if (html.includes(`text-${cls}`)) {
        color = hex;
        break;
      }
    }
    const isDisabled = className.includes('is-disabled');
    const svgPath = SVG_BASE_PATH + (isDisabled ? 'disabled-room-icon.svg' : 'room-icon.svg');
    return { kind: 'room-label', name, color, svgPath, size };
  }

  // Resource icon: has resource-icon-base class
  if (className.includes('resource-icon-base')) {
    const isDisabled = className.includes('is-disabled');
    const svgMap = isDisabled ? DISABLED_RESOURCE_TYPE_SVG : RESOURCE_TYPE_SVG;

    // Find which resource type matches from className
    const classes = className.split(/\s+/);
    for (const type of Object.keys(svgMap)) {
      if (classes.includes(type)) {
        return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + svgMap[type], size };
      }
    }

    // Also check inner HTML for icon class names (e.g., disabled-desk-icon, reserved-desk-icon)
    if (html.includes('disabled-desk-icon')) {
      return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + 'disabled-desk-icon.svg', size };
    }
    if (html.includes('disabled-parking-icon')) {
      return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + 'disabled-parking-icon.svg', size };
    }
    if (html.includes('reserved-desk-icon')) {
      return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + 'red-desk-icon.svg', size };
    }
    if (html.includes('reserved-parking-icon')) {
      return { kind: 'resource-icon', svgPath: SVG_BASE_PATH + 'red-parking-icon.svg', size };
    }

    // Default to desk icon if we can't determine type
    return {
      kind: 'resource-icon',
      svgPath: SVG_BASE_PATH + (isDisabled ? 'disabled-desk-icon.svg' : 'desk-icon.svg'),
      size,
    };
  }

  return { kind: 'unknown', size };
}

// --- Image loading ---

async function fetchImageSafe(url: string): Promise<HTMLImageElement | null> {
  // Try crossOrigin='anonymous' first (works if the server sends CORS headers,
  // which most CDNs/S3 do for anonymous requests). This avoids the fetch()
  // CORS preflight that fails when credentials don't match.
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('crossOrigin load failed'));
      el.src = url;
    });
    return img;
  } catch {
    // Fallback: fetch as blob to create a same-origin object URL
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(el);
        };
        el.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error(`Blob load failed: ${url}`));
        };
        el.src = objectUrl;
      });
    } catch {
      return null;
    }
  }
}

async function preloadImages(urls: Set<string>): Promise<Map<string, HTMLImageElement>> {
  const cache = new Map<string, HTMLImageElement>();
  await Promise.all(
    [...urls].map(async (url) => {
      const img = await fetchImageSafe(url);
      if (img) cache.set(url, img);
    }),
  );
  return cache;
}

// --- Coordinate projection ---

function makeProjector(leafletMap: LeafletMap, bounds: LatLngBounds, zoom: number) {
  const origin = leafletMap.project(bounds.getNorthWest(), zoom);
  return (latlng: LatLng) => {
    const p = leafletMap.project(latlng, zoom);
    return { x: p.x - origin.x, y: p.y - origin.y };
  };
}

// --- Drawing functions ---

function drawVectorLayers(
  ctx: CanvasRenderingContext2D,
  leafletMap: LeafletMap,
  toCanvas: (latlng: LatLng) => { x: number; y: number },
  mapScale: number,
): void {
  leafletMap.eachLayer((layer) => {
    if (!('getLatLngs' in layer)) return;
    const pathLayer = layer as Polygon | Polyline;
    const opts = pathLayer.options;
    const latlngs = pathLayer.getLatLngs();

    const isPolygon = layer instanceof leafletNs.Polygon;
    const rings: LatLng[][] = [];

    // Polygons return LatLng[][] (array of rings), polylines return LatLng[]
    if (Array.isArray(latlngs[0])) {
      (latlngs as LatLng[][]).forEach((ring) => {
        if (ring.length > 0) rings.push(ring);
      });
    } else {
      rings.push(latlngs as LatLng[]);
    }

    rings.forEach((ring) => {
      const points = ring.map((ll) => toCanvas(ll));
      if (points.length < 2) return;

      const first = points[0];
      if (!first) return;

      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < points.length; i++) {
        const pt = points[i];
        if (pt) ctx.lineTo(pt.x, pt.y);
      }
      if (isPolygon) ctx.closePath();

      if (opts.fill !== false && isPolygon) {
        ctx.globalAlpha = (opts.fillOpacity as number) ?? 0.2;
        ctx.fillStyle = (opts.fillColor as string) || (opts.color as string) || '#3388ff';
        ctx.fill();
      }

      if (opts.stroke !== false) {
        ctx.globalAlpha = (opts.opacity as number) ?? 1;
        ctx.strokeStyle = (opts.color as string) || '#3388ff';
        ctx.lineWidth = ((opts.weight as number) ?? 1.5) / mapScale;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    });
  });
}

function drawResourceIcon(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  size: number,
): void {
  ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
}

function drawMonogram(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  initials: string,
  bgColor: string,
  size: number,
): void {
  const radius = size / 2;
  // Match live map border width: clamp(size * 0.11, 1.75, 4.5)
  const borderWidth = Math.min(Math.max(size * 0.11, 1.75), 4.5);

  // White border
  ctx.beginPath();
  ctx.arc(x, y, radius + borderWidth / 2, 0, Math.PI * 2);
  ctx.fillStyle = 'white';
  ctx.fill();

  // Colored circle
  ctx.beginPath();
  ctx.arc(x, y, radius - 1, 0, Math.PI * 2);
  ctx.fillStyle = bgColor;
  ctx.fill();

  // Initials — proportional sizing matching live map (map-marker-scale.js)
  const innerSize = Math.max(size - borderWidth * 2, 10);
  const scale = initials.length >= 4 ? 0.38 : initials.length === 3 ? 0.46 : 0.54;
  const fontSize = Math.max(innerSize * scale, 6);
  const verticalOffset = fontSize * 0.16;
  ctx.fillStyle = 'white';
  ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials.toUpperCase(), x, y + verticalOffset);
}

function drawCurrentUser(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  label: string,
  size: number,
  labelDampen: number,
): void {
  drawResourceIcon(ctx, img, x, y, size);

  if (label) {
    // Match live map: font = max(14, min(18, size * 0.55)), margin = max(8, size * 0.3)
    const labelFontSize = Math.max(14, Math.min(18, Math.round(size * 0.55)));
    const labelMargin = Math.max(8, Math.round(size * 0.3));
    const labelX = x + size / 2 + labelMargin * labelDampen;
    ctx.font = `${labelFontSize * labelDampen}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#FA4338';
    ctx.fillText(label, labelX, y);
  }
}

function drawRoomLabel(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  name: string,
  color: string,
  size: number,
  labelDampen: number,
): void {
  drawResourceIcon(ctx, img, x, y, size);

  if (name) {
    const nameLen = name.length;
    const baseFontSize = nameLen <= 10 ? 14 : nameLen <= 20 ? 12 : nameLen <= 30 ? 10 : 8;
    const fontSize = baseFontSize * labelDampen;
    const lineHeight = (baseFontSize + 4) * labelDampen;
    const roomLabelGap = Math.min(Math.max(8 * labelDampen, 4), 12);

    ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = color;

    // Split into at most 2 lines at the nearest word boundary to center
    const lines = splitIntoTwoLines(name);

    let labelY = y + size / 2 + roomLabelGap;
    for (const line of lines) {
      ctx.fillText(line, x, labelY);
      labelY += lineHeight;
    }
  }
}

async function drawMarkerLayers(
  ctx: CanvasRenderingContext2D,
  leafletMap: LeafletMap,
  toCanvas: (latlng: LatLng) => { x: number; y: number },
  iconDampen: number,
  labelDampen: number,
  markerNames?: Record<string, string>,
): Promise<void> {
  // Phase 1: Classify all markers and collect URLs to preload
  const classified: Array<{ info: MarkerInfo; point: { x: number; y: number } }> = [];
  const imageUrls = new Set<string>();

  leafletMap.eachLayer((layer) => {
    if (!('getLatLng' in layer)) return;
    const marker = layer as Marker;
    if (!marker.getIcon) return;

    const info = classifyMarker(marker, markerNames);
    const point = toCanvas(marker.getLatLng());
    classified.push({ info, point });

    if (info.kind === 'resource-icon') imageUrls.add(info.svgPath);
    if (info.kind === 'current-user') imageUrls.add(info.svgPath);
    if (info.kind === 'room-label') imageUrls.add(info.svgPath);
  });

  // Phase 2: Preload all images in parallel
  const imageCache = await preloadImages(imageUrls);

  // Phase 3: Draw each marker with dampened sizes
  for (const { info, point } of classified) {
    const iconSize = info.size * iconDampen;
    const labelSize = info.size * labelDampen;
    switch (info.kind) {
      case 'resource-icon': {
        const img = imageCache.get(info.svgPath);
        if (img) {
          drawResourceIcon(ctx, img, point.x, point.y, iconSize);
        }
        break;
      }
      case 'current-user': {
        const cuImg = imageCache.get(info.svgPath);
        if (cuImg) {
          drawCurrentUser(ctx, cuImg, point.x, point.y, info.label, iconSize, labelDampen);
        }
        break;
      }
      case 'room-label': {
        const rlImg = imageCache.get(info.svgPath);
        if (rlImg) {
          drawRoomLabel(ctx, rlImg, point.x, point.y, info.name, info.color, labelSize, labelDampen);
        }
        break;
      }
      case 'monogram': {
        drawMonogram(ctx, point.x, point.y, info.initials, info.bgColor, iconSize);
        break;
      }
      case 'profile-picture': {
        const initials = deriveInitials(info.tooltipName);
        drawMonogram(ctx, point.x, point.y, initials || '?', 'hsl(224, 8%, 48%)', iconSize);
        break;
      }
      case 'unknown':
        break;
    }
  }
}

// --- Collision detection for auto-scaling ---

interface MarkerBBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

function bboxesOverlap(a: MarkerBBox, b: MarkerBBox): boolean {
  return !(a.x + a.width < b.x || b.x + b.width < a.x || a.y + a.height < b.y || b.y + b.height < a.y);
}

/** Returns the fraction of markers (0–1) that overlap with at least one other marker. */
function collisionRate(boxes: MarkerBBox[]): number {
  if (boxes.length === 0) return 0;
  const colliding = new Set<number>();
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      if (bboxesOverlap(boxes[i]!, boxes[j]!)) {
        colliding.add(i);
        colliding.add(j);
      }
    }
  }
  return colliding.size / boxes.length;
}

function computeMarkerBoxes(leafletMap: LeafletMap, bounds: LatLngBounds, sizeDampen: number): MarkerBBox[] {
  const zoom = leafletMap.getZoom();
  const toCanvas = makeProjector(leafletMap, bounds, zoom);
  const boxes: MarkerBBox[] = [];

  leafletMap.eachLayer((layer) => {
    if (!('getLatLng' in layer)) return;
    const marker = layer as Marker;
    if (!marker.getIcon) return;

    const info = classifyMarker(marker);
    // Skip unknown markers and room labels (labels are controlled by label slider, not icon slider)
    if (info.kind === 'unknown' || info.kind === 'room-label') return;

    const point = toCanvas(marker.getLatLng());
    const s = info.size * sizeDampen;

    let width = s;
    const height = s;

    if (info.kind === 'current-user' && info.label) {
      width = s + info.label.length * 10 * sizeDampen;
    }

    boxes.push({
      x: point.x - width / 2,
      y: point.y - height / 2,
      width,
      height,
    });
  });

  return boxes;
}

/**
 * Detect the optimal icon scale that avoids marker collisions.
 * Starts at scale 1.0 and reduces by 2.5% per step until no more
 * than 3% of markers overlap, down to a minimum of 0.1x.
 */
export function detectOptimalIconScale(leafletMap: LeafletMap, bounds: LatLngBounds, mapScale: number): number {
  let iconScale = mapScale;
  const minScale = 0.1;
  const step = 0.975; // shrink by 2.5% each iteration
  const collisionTolerance = 0.03; // allow up to 3% of markers to overlap

  for (let i = 0; i < 200; i++) {
    const sizeDampen = iconScale / mapScale;
    const boxes = computeMarkerBoxes(leafletMap, bounds, sizeDampen);
    if (collisionRate(boxes) <= collisionTolerance) return iconScale;
    iconScale *= step;
    if (iconScale < minScale) return minScale;
  }

  return iconScale;
}

// --- Render to data URL (for preview) ---

export async function renderMapToDataUrl(opts: RenderMapOptions): Promise<string> {
  const { leafletMap, bounds, mapScale = 1, iconScale = 1, labelScale, markerNames } = opts;
  const zoom = leafletMap.getZoom();

  const sw = leafletMap.project(bounds.getSouthWest(), zoom);
  const ne = leafletMap.project(bounds.getNorthEast(), zoom);
  const logicalWidth = Math.round(Math.abs(ne.x - sw.x));
  const logicalHeight = Math.round(Math.abs(ne.y - sw.y));

  const toCanvas = makeProjector(leafletMap, bounds, zoom);

  const canvasWidth = logicalWidth * mapScale;
  const canvasHeight = logicalHeight * mapScale;
  const MAX_CANVAS_PIXELS = 268_000_000; // Chrome/Safari area limit
  const MAX_CANVAS_DIMENSION = 16_384; // Chrome/Safari per-axis limit
  if (canvasWidth > MAX_CANVAS_DIMENSION || canvasHeight > MAX_CANVAS_DIMENSION) {
    throw new Error(
      `Image would be ${Math.round(canvasWidth)}×${Math.round(canvasHeight)} pixels — ` +
        `each side must be under ${MAX_CANVAS_DIMENSION}. Reduce the map scale and try again.`,
    );
  }
  if (canvasWidth * canvasHeight > MAX_CANVAS_PIXELS) {
    throw new Error(
      `Image would be ${Math.round(canvasWidth)}×${Math.round(
        canvasHeight,
      )} pixels, which exceeds browser limits. Reduce the map scale and try again.`,
    );
  }

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create canvas context');

  // Scale coordinates so drawing operates in logical space
  ctx.scale(mapScale, mapScale);

  // 1. Background
  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(0, 0, logicalWidth, logicalHeight);

  // 2. Raster floor plan image (use the first image overlay found)
  let rasterUrl: string | null = null;
  leafletMap.eachLayer((layer) => {
    if (rasterUrl) return; // already found
    const layerAny = layer as unknown as { _url?: string; _bounds?: unknown; getElement?: () => HTMLImageElement };
    if (layerAny._url && layerAny._bounds) {
      rasterUrl = layerAny.getElement?.()?.src || layerAny._url;
    }
  });

  if (rasterUrl) {
    const imgEl = await fetchImageSafe(rasterUrl);
    if (imgEl) {
      const imgNW = toCanvas(bounds.getNorthWest());
      const imgSE = toCanvas(bounds.getSouthEast());
      ctx.drawImage(imgEl, imgNW.x, imgNW.y, imgSE.x - imgNW.x, imgSE.y - imgNW.y);
    }
  }

  // 3. Vector layers (neighborhood polygons, polylines)
  drawVectorLayers(ctx, leafletMap, toCanvas, mapScale);

  // 4. Markers — iconScale and labelScale control sizes relative to the map.
  // Each dampen factor adjusts sizes in logical coordinates so they don't
  // scale 1:1 with mapScale.
  const iconDampen = iconScale / mapScale;
  const labelDampen = (labelScale ?? iconScale) / mapScale;
  await drawMarkerLayers(ctx, leafletMap, toCanvas, iconDampen, labelDampen, markerNames);

  const dataUrl = canvas.toDataURL('image/png');
  if (!dataUrl || dataUrl === 'data:,') {
    throw new Error('Export produced a blank image. Try reducing the map scale.');
  }
  return dataUrl;
}

// --- SVG export ---

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function imageToDataUrl(img: HTMLImageElement): string {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const ctx = c.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(img, 0, 0);
  try {
    return c.toDataURL('image/png');
  } catch {
    return '';
  }
}

async function fetchAsDataUrl(url: string): Promise<string> {
  // For SVG files, fetch as text and encode directly
  if (url.endsWith('.svg')) {
    try {
      const response = await fetch(url);
      if (!response.ok) return '';
      const text = await response.text();
      // Encode to UTF-8 bytes before btoa — bare btoa throws InvalidCharacterError
      // on any character > 0xFF (curly quotes, em-dashes, BOM, etc. that frequently
      // appear in floor-plan SVGs).
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(text)))}`;
    } catch {
      return '';
    }
  }
  // For raster images, load and convert via canvas
  const img = await fetchImageSafe(url);
  if (!img) return '';
  return imageToDataUrl(img);
}

function svgPolygonPoints(latlngs: LatLng[], toCanvas: (latlng: LatLng) => { x: number; y: number }): string {
  return latlngs
    .map((ll) => {
      const p = toCanvas(ll);
      return `${p.x},${p.y}`;
    })
    .join(' ');
}

/** Split text into at most 2 lines at the nearest word boundary to the middle */
export function splitIntoTwoLines(text: string): string[] {
  const words = text.split(/\s+/);
  if (words.length <= 1) return [text];

  const mid = text.length / 2;
  let bestSplit = 0;
  let bestDist = Infinity;
  let pos = 0;

  for (let i = 0; i < words.length - 1; i++) {
    pos += words[i]!.length + (i > 0 ? 1 : 0);
    const dist = Math.abs(pos - mid);
    if (dist < bestDist) {
      bestDist = dist;
      bestSplit = i + 1;
    }
  }

  const line1 = words.slice(0, bestSplit).join(' ');
  const line2 = words.slice(bestSplit).join(' ');
  return line2 ? [line1, line2] : [line1];
}

export async function renderMapToSvg(opts: RenderMapOptions): Promise<string> {
  const { leafletMap, bounds, iconScale = 1, labelScale, markerNames } = opts;
  // SVG is vector — mapScale only affects the viewBox, not resolution
  const zoom = leafletMap.getZoom();

  const sw = leafletMap.project(bounds.getSouthWest(), zoom);
  const ne = leafletMap.project(bounds.getNorthEast(), zoom);
  const width = Math.round(Math.abs(ne.x - sw.x));
  const height = Math.round(Math.abs(ne.y - sw.y));

  const toCanvas = makeProjector(leafletMap, bounds, zoom);
  const iconDampen = iconScale;
  const labelDampen = labelScale ?? iconScale;

  const elements: string[] = [];

  // 1. Background
  elements.push(`<rect width="${width}" height="${height}" fill="#f5f5f5"/>`);

  // 2. Raster floor plan
  let rasterUrl: string | null = null;
  leafletMap.eachLayer((layer) => {
    if (rasterUrl) return;
    const layerAny = layer as unknown as { _url?: string; _bounds?: unknown; getElement?: () => HTMLImageElement };
    if (layerAny._url && layerAny._bounds) {
      rasterUrl = layerAny.getElement?.()?.src || layerAny._url;
    }
  });

  if (rasterUrl) {
    const dataUrl = await fetchAsDataUrl(rasterUrl);
    if (dataUrl) {
      const nw = toCanvas(bounds.getNorthWest());
      const se = toCanvas(bounds.getSouthEast());
      const w = se.x - nw.x;
      const h = se.y - nw.y;
      elements.push(`<image href="${dataUrl}" x="${nw.x}" y="${nw.y}" width="${w}" height="${h}"/>`);
    }
  }

  // 3. Vector layers (polygons/polylines)
  leafletMap.eachLayer((layer) => {
    if (!('getLatLngs' in layer)) return;
    const pathLayer = layer as Polygon | Polyline;
    const o = pathLayer.options;
    const latlngs = pathLayer.getLatLngs();
    const isPolygon = layer instanceof leafletNs.Polygon;
    const rings: LatLng[][] = [];

    if (Array.isArray(latlngs[0])) {
      (latlngs as LatLng[][]).forEach((ring) => {
        if (ring.length > 0) rings.push(ring);
      });
    } else {
      rings.push(latlngs as LatLng[]);
    }

    rings.forEach((ring) => {
      const points = svgPolygonPoints(ring, toCanvas);
      const strokeColor = (o.color as string) || '#3388ff';
      const filled = isPolygon && (o as { fill?: boolean }).fill !== false;
      const stroked = (o as { stroke?: boolean }).stroke !== false;
      const strokeW = (o.weight as number) ?? 1.5;
      const strokeOpacityValue = (o.opacity as number) ?? 1;
      const strokeOpacity = stroked ? strokeOpacityValue : 0;
      const fillOpacityValue = (o.fillOpacity as number) ?? 0.2;
      const fill = filled ? (o.fillColor as string) || strokeColor : 'none';
      const fillOpacity = filled ? fillOpacityValue : 0;
      const stroke = stroked ? strokeColor : 'none';
      const tag = isPolygon ? 'polygon' : 'polyline';
      elements.push(
        `<${tag} points="${points}" fill="${escapeXml(fill)}" fill-opacity="${fillOpacity}" stroke="${escapeXml(
          stroke,
        )}" stroke-width="${strokeW}" stroke-opacity="${strokeOpacity}"/>`,
      );
    });
  });

  // 4. Classify markers and collect icon URLs
  const classified: Array<{ info: MarkerInfo; point: { x: number; y: number } }> = [];
  const iconUrls = new Set<string>();

  leafletMap.eachLayer((layer) => {
    if (!('getLatLng' in layer)) return;
    const marker = layer as Marker;
    if (!marker.getIcon) return;

    const info = classifyMarker(marker, markerNames);
    const point = toCanvas(marker.getLatLng());
    classified.push({ info, point });

    if (info.kind === 'resource-icon') iconUrls.add(info.svgPath);
    if (info.kind === 'current-user') iconUrls.add(info.svgPath);
    if (info.kind === 'room-label') iconUrls.add(info.svgPath);
  });

  // Preload icon images as data URLs for embedding
  const iconDataUrls = new Map<string, string>();
  await Promise.all(
    [...iconUrls].map(async (url) => {
      const dataUrl = await fetchAsDataUrl(url);
      if (dataUrl) iconDataUrls.set(url, dataUrl);
    }),
  );

  // 5. Draw markers
  const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  for (const { info, point } of classified) {
    const iconSize = info.size * iconDampen;
    const labelSize = info.size * labelDampen;

    switch (info.kind) {
      case 'resource-icon': {
        const href = iconDataUrls.get(info.svgPath);
        if (href) {
          elements.push(
            `<image href="${href}" x="${point.x - iconSize / 2}" y="${
              point.y - iconSize / 2
            }" width="${iconSize}" height="${iconSize}"/>`,
          );
        }
        break;
      }
      case 'monogram':
      case 'profile-picture': {
        const initials = info.kind === 'monogram' ? info.initials : deriveInitials(info.tooltipName) || '?';
        const bgColor = info.kind === 'monogram' ? info.bgColor : 'hsl(224, 8%, 48%)';
        const radius = iconSize / 2;
        const borderWidth = Math.min(Math.max(iconSize * 0.11, 1.75), 4.5);
        const innerSize = Math.max(iconSize - borderWidth * 2, 10);
        const scale = initials.length >= 4 ? 0.38 : initials.length === 3 ? 0.46 : 0.54;
        const fontSize = Math.max(innerSize * scale, 6);
        const verticalOffset = fontSize * 0.16;
        // White border circle
        elements.push(`<circle cx="${point.x}" cy="${point.y}" r="${radius + borderWidth / 2}" fill="white"/>`);
        // Colored circle
        elements.push(`<circle cx="${point.x}" cy="${point.y}" r="${radius - 1}" fill="${escapeXml(bgColor)}"/>`);
        // Initials text
        elements.push(
          `<text x="${point.x}" y="${
            point.y + verticalOffset
          }" text-anchor="middle" dominant-baseline="central" fill="white" font-family="${FONT}" font-size="${fontSize}" font-weight="600">${escapeXml(
            initials.toUpperCase(),
          )}</text>`,
        );
        break;
      }
      case 'current-user': {
        const href = iconDataUrls.get(info.svgPath);
        if (href) {
          elements.push(
            `<image href="${href}" x="${point.x - iconSize / 2}" y="${
              point.y - iconSize / 2
            }" width="${iconSize}" height="${iconSize}"/>`,
          );
        }
        if (info.label) {
          const labelFontSize = Math.max(14, Math.min(18, Math.round(iconSize * 0.55))) * labelDampen;
          const labelMargin = Math.max(8, Math.round(iconSize * 0.3)) * labelDampen;
          const labelX = point.x + iconSize / 2 + labelMargin;
          elements.push(
            `<text x="${labelX}" y="${
              point.y
            }" dominant-baseline="central" fill="#FA4338" font-family="${FONT}" font-size="${labelFontSize}">${escapeXml(
              info.label,
            )}</text>`,
          );
        }
        break;
      }
      case 'room-label': {
        const href = iconDataUrls.get(info.svgPath);
        if (href) {
          elements.push(
            `<image href="${href}" x="${point.x - labelSize / 2}" y="${
              point.y - labelSize / 2
            }" width="${labelSize}" height="${labelSize}"/>`,
          );
        }
        if (info.name) {
          const nameLen = info.name.length;
          const baseFontSize = nameLen <= 10 ? 14 : nameLen <= 20 ? 12 : nameLen <= 30 ? 10 : 8;
          const fontSize = baseFontSize * labelDampen;
          const lineHeight = (baseFontSize + 4) * labelDampen;
          const roomLabelGap = Math.min(Math.max(8 * labelDampen, 4), 12);
          const lines = splitIntoTwoLines(info.name);
          const startY = point.y + labelSize / 2 + roomLabelGap + fontSize;

          lines.forEach((line, i) => {
            elements.push(
              `<text x="${point.x}" y="${startY + i * lineHeight}" text-anchor="middle" fill="${escapeXml(
                info.color,
              )}" font-family="${FONT}" font-size="${fontSize}">${escapeXml(line)}</text>`,
            );
          });
        }
        break;
      }
      case 'unknown':
        break;
    }
  }

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">`,
    ...elements,
    '</svg>',
  ].join('\n');
}

export function downloadSvg(svgString: string, fileName: string): void {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  saveAs(blob, fileName);
}

// --- Download a rendered data URL ---

export function downloadDataUrl(dataUrl: string, fileName: string): void {
  // Convert data URL to blob for efficient download
  const parts = dataUrl.split(',');
  if (parts.length < 2 || !parts[0] || !parts[1]) return;
  const byteString = atob(parts[1]);
  const mimeString = parts[0].split(':')[1]?.split(';')[0] || 'image/png';
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeString });
  saveAs(blob, fileName);
}

export interface PdfOptions {
  pageSize: string;
  orientation: 'portrait' | 'landscape';
  output: 'single' | 'multiple';
  fitToPage: boolean;
  scale: number;
}

export const PAGE_DIMENSIONS: Record<string, { w: number; h: number; label: string }> = {
  letter: { w: 215.9, h: 279.4, label: 'Letter (8.5 × 11 in)' },
  legal: { w: 215.9, h: 355.6, label: 'Legal (8.5 × 14 in)' },
  tabloid: { w: 279.4, h: 431.8, label: 'Tabloid (11 × 17 in)' },
  a4: { w: 210, h: 297, label: 'A4 (210 × 297 mm)' },
  a3: { w: 297, h: 420, label: 'A3 (297 × 420 mm)' },
  a2: { w: 420, h: 594, label: 'A2 (420 × 594 mm)' },
  a1: { w: 594, h: 841, label: 'A1 (594 × 841 mm)' },
  a0: { w: 841, h: 1189, label: 'A0 (841 × 1189 mm)' },
};

export async function downloadPdf(opts: RenderMapOptions, pdfOpts: PdfOptions, fileName: string): Promise<void> {
  const page = PAGE_DIMENSIONS[pdfOpts.pageSize] || PAGE_DIMENSIONS['letter']!;
  const pageW = pdfOpts.orientation === 'landscape' ? page.h : page.w;
  const pageH = pdfOpts.orientation === 'landscape' ? page.w : page.h;
  const margin = 12.7; // 0.5 inch — standard printer minimum
  const contentW = pageW - margin * 2;
  const contentH = pageH - margin * 2;

  const { leafletMap, bounds } = opts;
  const zoom = leafletMap.getZoom();
  const sw = leafletMap.project(bounds.getSouthWest(), zoom);
  const ne = leafletMap.project(bounds.getNorthEast(), zoom);
  const logicalW = Math.abs(ne.x - sw.x);
  const logicalH = Math.abs(ne.y - sw.y);
  if (logicalW <= 0 || logicalH <= 0) {
    throw new Error('Map area is empty — cannot generate PDF');
  }

  let mapScale: number;
  let totalImgW: number;
  let totalImgH: number;

  if (pdfOpts.output === 'single' && pdfOpts.fitToPage) {
    // Fit to page: scale map to fill the content area
    const fitScale = Math.min(contentW / logicalW, contentH / logicalH);
    totalImgW = logicalW * fitScale;
    totalImgH = logicalH * fitScale;
    // Render at high DPI for print quality
    mapScale = fitScale * (150 / 25.4);
  } else if (pdfOpts.output === 'single') {
    // Single page, no fit: render at native size (1px = 1/150 inch)
    const dpi = 150;
    totalImgW = logicalW / (dpi / 25.4);
    totalImgH = logicalH / (dpi / 25.4);
    mapScale = 1;
  } else {
    // Multiple pages: user-controlled scale (clamp defensively in case caller didn't)
    mapScale = Math.max(0.1, Number(pdfOpts.scale)) || 1;
    const dpi = 150;
    totalImgW = (logicalW * mapScale) / (dpi / 25.4);
    totalImgH = (logicalH * mapScale) / (dpi / 25.4);
  }

  // For fit-to-page, mapScale is derived from fitScale (not user input).
  // Scale icons/labels proportionally so they render at the same visual
  // size as the preview (where iconDampen = iconScale directly).
  // For other modes, the component already scaled iconScale by s.
  const renderIconScale =
    pdfOpts.output === 'single' && pdfOpts.fitToPage ? (opts.iconScale ?? 1) * mapScale : opts.iconScale;
  const renderLabelScale =
    pdfOpts.output === 'single' && pdfOpts.fitToPage
      ? (opts.labelScale ?? opts.iconScale ?? 1) * mapScale
      : opts.labelScale;
  const dataUrl = await renderMapToDataUrl({
    ...opts,
    mapScale,
    iconScale: renderIconScale,
    labelScale: renderLabelScale,
  });

  const pdf = new jsPDF({
    orientation: pdfOpts.orientation,
    unit: 'mm',
    format: pdfOpts.pageSize,
  });

  if (pdfOpts.output === 'single') {
    // Center on single page
    const offsetX = margin + (contentW - totalImgW) / 2;
    const offsetY = margin + (contentH - totalImgH) / 2;
    pdf.addImage(dataUrl, 'PNG', offsetX, offsetY, totalImgW, totalImgH);
  } else {
    // Tile across multiple pages
    const cols = Math.ceil(totalImgW / contentW);
    const rows = Math.ceil(totalImgH / contentH);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (row > 0 || col > 0) {
          pdf.addPage(pdfOpts.pageSize, pdfOpts.orientation);
        }
        // Clip to content area so image doesn't bleed into margins.
        // Use moveTo/lineTo path (not rect) to avoid drawing a visible border.
        pdf.saveGraphicsState();
        pdf.moveTo(margin, margin);
        pdf.lineTo(margin + contentW, margin);
        pdf.lineTo(margin + contentW, margin + contentH);
        pdf.lineTo(margin, margin + contentH);
        pdf.lineTo(margin, margin); // close the path
        pdf.clip();
        pdf.discardPath();
        const imgX = margin - col * contentW;
        const imgY = margin - row * contentH;
        pdf.addImage(dataUrl, 'PNG', imgX, imgY, totalImgW, totalImgH, 'map-tile');
        pdf.restoreGraphicsState();
      }
    }
  }

  pdf.save(fileName);
}
